import React, { useState, useEffect } from 'react';
import { setActiveWorkflowSource, getMyTenantStandards } from '../api';
import {
  FileText,
  BookOpen,
  Users,
  Zap,
  MessageSquare,
  GitBranch,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Loader,
  Lock,
  Upload,
  ChevronUp,
  ChevronDown,
  ClipboardList,
  X
} from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import { projectInactiveReason, projectInactiveLabel } from '../utils/projectStatus';
import { showApiError } from '../utils/apiError';
import toast from 'react-hot-toast';
import Analysis from './Analysis';
import Documents from './Documents';
import TerminologyAnalysis from '../components/Analysis/TerminologyAnalysis';
import StakeholdersSystemsTab from '../components/Analysis/StakeholdersSystemsTab';
import SOPGenerationTab from '../components/Analysis/SOPGenerationTab';
import Interviews from './Interviews';
import SOPUpdateChange from '../components/SOPBuilder/SOPUpdateChange';
import ProcessMaps from './ProcessMaps';
import CollaboratorsList from '../components/Collaborators/CollaboratorsList';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { useOrchestratorProjectGuard } from '../hooks/useOrchestratorProjectGuard';
import OrchestratorProjectMismatchBanner from '../components/SmartRouter/OrchestratorProjectMismatchBanner';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';
import { STEP_ORDER, NON_BLOCKING_STEPS, firstIncompleteBlocker } from './sopBuilderGating';



interface ApprovedProcessMapInfo {
  id: number;
  level: number;
  validation_status: string | null;
  approved_at: string | null;
  approved_by: number | null;
  updated_at: string;
}

const SOPBuilder: React.FC = () => {
  const { currentProject, loading } = useProject();
  const orchestratorCtx = useOrchestratorContext();
  const orchestratorProject = useOrchestratorProjectGuard();
  const smartRouter = useSmartRouterStepSync();

  const [tenantDocs, setTenantDocs] = useState<{ id: number; original_name: string; file_size: number; processing_status: string }[]>([]);
  const [tenantDocsLoading, setTenantDocsLoading] = useState(true);
  const [tenantDocsError, setTenantDocsError] = useState<string | null>(null);
  const [excludedOrgStandardIds, setExcludedOrgStandardIds] = useState<Set<number>>(new Set());
  const [isOrgStandardsPanelOpen, setIsOrgStandardsPanelOpen] = useState(false);

  useEffect(() => {
    setTenantDocsLoading(true);
    setTenantDocsError(null);
    const projectId = currentProject?.id;
    getMyTenantStandards(projectId)
      .then((res: { documents: { id: number; original_name: string; file_size: number; processing_status: string }[] }) => {
        setTenantDocs(res.documents);
      })
      .catch((err: any) => {
        setTenantDocsError(err?.response?.data?.error || err?.message || 'Failed to load standards');
      })
      .finally(() => setTenantDocsLoading(false));
  }, [currentProject?.id]);

  // While the user is inside the SOP Builder workflow, every downstream API
  // call carries an `X-Source-Workflow: sop_builder` header so backend
  // middleware can re-tag spend under module='SOP Builder' even when the
  // underlying API lives in Process Intelligence / Document Intelligence.
  // Cleared on unmount so other pages (standalone Process Maps, etc.) keep
  // their normal module tags.
  useEffect(() => {
    setActiveWorkflowSource('sop_builder');
    return () => setActiveWorkflowSource(null);
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    if (orchestratorCtx.inRouter && orchestratorCtx.subStep && STEP_ORDER.includes(orchestratorCtx.subStep)) {
      return orchestratorCtx.subStep;
    }
    return 'add-documents';
  });
  const [analysisResults, setAnalysisResults] = React.useState<any>(null);
  const [approvedMapInfo, setApprovedMapInfo] = React.useState<ApprovedProcessMapInfo | null>(null);
  const [approving, setApproving] = React.useState(false);
  const [stakeholdersLoading, setStakeholdersLoading] = React.useState(true);
  const [projectInstructions, setProjectInstructions] = React.useState('');
  const [editingProjectInstructions, setEditingProjectInstructions] = React.useState(false);
  const [savingProjectInstructions, setSavingProjectInstructions] = React.useState(false);
  const [isProjectInstructionsCollapsed, setIsProjectInstructionsCollapsed] = React.useState(true);

  React.useEffect(() => {
    if (orchestratorCtx.inRouter && orchestratorCtx.subStep && STEP_ORDER.includes(orchestratorCtx.subStep)) {
      setActiveTab(orchestratorCtx.subStep);
    }
  }, [orchestratorCtx.inRouter, orchestratorCtx.subStep]);

  // Track completed steps — persisted to backend workflow_state per project (not localStorage)
  // NOTE: this set means "progressed past" and is what unlocks later steps. It is
  // deliberately NOT what the green check reads — see isStepComplete.
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [terminologySummary, setTerminologySummary] = useState<{ total: number; approved: number } | null>(null);
  const completedStepsRef = React.useRef(completedSteps);
  React.useEffect(() => { completedStepsRef.current = completedSteps; }, [completedSteps]);
  // Guard: keep steps locked until DB load completes (prevents flash-of-unlocked-steps)
  const [workflowStateLoaded, setWorkflowStateLoaded] = React.useState(false);

  // Helper: save workflow state to backend
  const saveWorkflowState = React.useCallback(async (patch: Record<string, any>) => {
    if (!currentProject) return;
    try {
      const api = (await import('../api')).default;
      await api.put(`/api/projects/${currentProject.id}/workflow-state`, patch);
    } catch (e) {
      console.error('Failed to save workflow state:', e);
    }
  }, [currentProject]);


  React.useEffect(() => {
    if (!currentProject) return;
    setWorkflowStateLoaded(false);
    setProjectInstructions(currentProject.analysis_instructions || '');
    // Clean up legacy localStorage keys
    try {
      localStorage.removeItem(`sop_builder_completed_steps_v2_${currentProject.id}`);
      localStorage.removeItem(`sop_builder_sop_stale_${currentProject.id}`);
    } catch {}
    (async () => {
      try {
        const api = (await import('../api')).default;
        const res = await api.get(`/api/projects/${currentProject.id}/workflow-state`);
        const state = res.data?.workflow_state || {};
        const savedSteps: string[] = Array.isArray(state.completed_steps) ? state.completed_steps : [];
        // Migration for the step reorder (approve-terminology now precedes generate-sop):
        // a project completed under the OLD order may have 'generate-sop' done but never
        // 'approve-terminology'. Under the new lock rule that would re-lock a finished SOP
        // step. Backfill terminology as complete so existing projects aren't regressed.
        const migratedSteps = savedSteps.includes('generate-sop') && !savedSteps.includes('approve-terminology')
          ? [...savedSteps, 'approve-terminology']
          : savedSteps;
        setCompletedSteps(new Set(migratedSteps));
        if (migratedSteps.length !== savedSteps.length) {
          saveWorkflowState({ completed_steps: migratedSteps });
        }
        const savedExcludedStandards: number[] = Array.isArray(state.excluded_org_standard_ids)
          ? state.excluded_org_standard_ids
          : [];
        setExcludedOrgStandardIds(new Set(savedExcludedStandards));
      } catch {
        setCompletedSteps(new Set());
        setExcludedOrgStandardIds(new Set());
      } finally {
        setWorkflowStateLoaded(true);
      }
    })();
  }, [currentProject?.id, currentProject?.analysis_instructions]);

  const markStepCompleted = React.useCallback((stepId: string) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.add(stepId);
      saveWorkflowState({ completed_steps: Array.from(next) });
      return next;
    });
  }, [saveWorkflowState]);

  const toggleOrgStandard = React.useCallback((docId: number) => {
    setExcludedOrgStandardIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      saveWorkflowState({ excluded_org_standard_ids: Array.from(next) });
      return next;
    });
  }, [saveWorkflowState]);

  const saveProjectInstructions = React.useCallback(async () => {
    if (!currentProject) return;
    setSavingProjectInstructions(true);
    try {
      const api = (await import('../api')).default;
      await api.put(`/api/projects/${currentProject.id}`, {
        analysis_instructions: projectInstructions
      });
      setEditingProjectInstructions(false);
      toast.success('Project instructions saved!');
    } catch (error: any) {
      showApiError(error, 'Failed to save project instructions');
    } finally {
      setSavingProjectInstructions(false);
    }
  }, [currentProject, projectInstructions]);

  // Auto-mark step as completed when user visits it (and data is available)
  React.useEffect(() => {
    if (!analysisResults) return;

    const stepId = activeTab;
    const stepIdx = STEP_ORDER.indexOf(stepId);
    if (stepIdx === -1) return;


    const canComplete = (() => {
      switch (stepId) {
        case 'add-documents':
        case 'review-summaries':
          return analysisResults?.document_analyses?.length > 0;
        case 'approve-stakeholders':
          return !!analysisResults?.process_synthesis;
        case 'approve-terminology':
          // Terminology is auto-extracted from source documents right after
          // Process Synthesis completes — it does not depend on the SOP.
          return !!analysisResults?.process_synthesis;
        case 'generate-sop':
          return !!analysisResults?.process_synthesis;
        case 'fill-gaps':
        case 'update-change':
          return !!analysisResults?.sop;
        default:
          return false;
      }
    })();

    if (canComplete && !completedSteps.has(stepId)) {
      markStepCompleted(stepId);
    }
  }, [activeTab, analysisResults]);

  // Load analysis results function
  const loadAnalysisResults = React.useCallback(async () => {
    if (!currentProject) return;
    try {
      const api = (await import('../api')).default;
      // Every step gate above Step 1 reads analysisResults, so a request left
      // hanging on the api client's five-minute default leaves the whole wizard
      // showing "Complete previous steps" on work that is already done. Bounded
      // to the same 30s the approval call uses: the 30-second refresh below then
      // retries, instead of the screen sitting locked until the request gives up.
      const response = await api.get(`/api/analysis/results/${currentProject.id}`, { timeout: 30000 });
      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Failed to load analysis results:', error);
    }
  }, [currentProject]);

  const loadProcessMapApproval = React.useCallback(async () => {
    if (!currentProject) return;
    try {
      const api = (await import('../api')).default;
      const response = await api.get(`/api/analysis/projects/${currentProject.id}/process-maps`, { timeout: 30000 });
      const maps: any[] = response.data?.process_maps || [];

      // Accept any level map, but prioritize L3 > L2 > L1
      const latestMap = maps.sort((a, b) => {
        const levelPriority: Record<number, number> = { 3: 0, 2: 1, 1: 2 };
        const aPriority = levelPriority[a.level as number] ?? 999;
        const bPriority = levelPriority[b.level as number] ?? 999;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      })[0];

      if (!latestMap) {
        setApprovedMapInfo(null);
        return;
      }

      const info: ApprovedProcessMapInfo = {
        id: latestMap.id,
        level: latestMap.level,
        validation_status: latestMap.validation_status || null,
        approved_at: latestMap.approved_at || null,
        approved_by: latestMap.approved_by || null,
        updated_at: latestMap.updated_at
      };
      setApprovedMapInfo(info);

      if (info.validation_status === 'validated' && !completedSteps.has('validate-process-map')) {
        markStepCompleted('validate-process-map');
      } else if (info.validation_status !== 'validated' && completedSteps.has('validate-process-map')) {
        setCompletedSteps(prev => {
          const next = new Set(prev);
          next.delete('validate-process-map');
          saveWorkflowState({ completed_steps: Array.from(next) });
          return next;
        });
      }
      return info;
    } catch (error) {
      console.error('Failed to load process map approval state:', error);
      return null;
    }
  }, [currentProject, completedSteps, saveWorkflowState]);

  // The stepper needs the approved/total split to tell a genuinely finished
  // Approve Terminology step from one that has merely been opened.
  const loadTerminologySummary = React.useCallback(async () => {
    if (!currentProject) return;
    try {
      const api = (await import('../api')).default;
      const response = await api.get(`/api/terminology/${currentProject.id}/summary`, { timeout: 30000 });
      const summary = response.data?.data?.summary;
      setTerminologySummary(summary ? {
        total: Number(summary.total_terms) || 0,
        approved: Number(summary.approved_terms) || 0,
      } : null);
    } catch (error) {
      // Non-fatal: without counts the step simply never claims to be complete.
      setTerminologySummary(null);
    }
  }, [currentProject]);

  // Load analysis results on mount and when project changes
  React.useEffect(() => {
    if (currentProject) {
      loadAnalysisResults();
      loadProcessMapApproval();
      loadTerminologySummary();
    }
  }, [currentProject, loadAnalysisResults, loadProcessMapApproval, loadTerminologySummary]);

  // Auto-refresh analysis results every 30 seconds to detect new data
  React.useEffect(() => {
    if (!currentProject) return;

    const interval = setInterval(() => {
      loadAnalysisResults();
      loadProcessMapApproval();
      loadTerminologySummary();
    }, 30000);

    return () => clearInterval(interval);
  }, [currentProject, loadAnalysisResults, loadProcessMapApproval, loadTerminologySummary]);

  // Listen for process map updates to refresh synthesis/stakeholder data
  React.useEffect(() => {
    const handleProcessMapsUpdated = () => {
      console.log('🔄 [SOPBuilder] Process map updated, refreshing analysis results...');
      loadAnalysisResults();
      loadProcessMapApproval();
    };

    window.addEventListener('process-maps-updated', handleProcessMapsUpdated as EventListener);

    return () => {
      window.removeEventListener('process-maps-updated', handleProcessMapsUpdated as EventListener);
    };
  }, [loadAnalysisResults, loadProcessMapApproval]);

  const handleApproveProcessMap = async () => {
    setApproving(true);
    try {
      const target = approvedMapInfo || (await loadProcessMapApproval());
      if (!target) {
        toast.error('No process map found to approve — reload the page and try again.');
        return;
      }
      const api = (await import('../api')).default;
      await api.post(`/api/analysis/process-maps/${target.id}/approve`, {}, { timeout: 30000 });
      await loadProcessMapApproval();
      toast.success('Process map approved');
    } catch (error: any) {
      console.error('Failed to approve process map:', error);
      showApiError(error, 'Failed to approve process map. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleUnapproveProcessMap = async () => {
    setApproving(true);
    try {
      const target = approvedMapInfo || (await loadProcessMapApproval());
      if (!target) {
        toast.error('No process map found — reload the page and try again.');
        return;
      }
      const api = (await import('../api')).default;
      await api.post(`/api/analysis/process-maps/${target.id}/unapprove`, {}, { timeout: 30000 });
      await loadProcessMapApproval();
      toast.success('Approval revoked');
    } catch (error: any) {
      console.error('Failed to unapprove process map:', error);
      showApiError(error, 'Failed to revoke approval. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  // Check if a step has the required data available
  const hasRequiredData = (stepId: string): boolean => {
    if (!analysisResults) return stepId === 'review-summaries' || stepId === 'add-documents';

    switch (stepId) {
      case 'add-documents':
      case 'review-summaries':
        return true; // Always accessible
      case 'approve-stakeholders':
      case 'validate-process-map':
      case 'generate-sop':
      case 'approve-terminology': // Auto-extracted from documents right after synthesis
        return !!analysisResults?.process_synthesis;
      case 'fill-gaps':
      case 'update-change':
        return !!analysisResults?.sop;
      default:
        return false;
    }
  };

  // Step status: a step unlocks once every blocking step before it is complete
  // and its own data dependency is met. NON_BLOCKING_STEPS are skipped in that
  // check, so generate-sop still waits on approve-terminology while nothing
  // waits on review-summaries or validate-process-map.
  const getStepStatus = (stepId: string): 'completed' | 'available' | 'locked' => {
    // Step 0 (Add Documents) and Step 1 (Review Summaries) are always available.
    // Keep everything else locked until workflow state is loaded from DB to
    // prevent a flash of unlocked steps on initial render (completedSteps = empty Set)
    if (!workflowStateLoaded && stepId !== 'add-documents' && stepId !== 'review-summaries') return 'locked';

    // Step 0: Add Documents — always available; marked complete once documents are analysed
    if (stepId === 'add-documents') {
      return analysisResults?.document_analyses?.length > 0 ? 'completed' : 'available';
    }

    const stepIdx = STEP_ORDER.indexOf(stepId);

    // Step 1: Review Summaries — always available
    if (stepId === 'review-summaries') {
      return analysisResults?.document_analyses?.length > 0 && completedSteps.has(stepId)
        ? 'completed'
        : 'available';
    }

    const previousSteps = STEP_ORDER.slice(0, stepIdx).filter(
      (s) => !NON_BLOCKING_STEPS.includes(s)
    );

    const allPreviousCompleted = previousSteps.every(
      prevStep => completedSteps.has(prevStep)
    );

    if (!allPreviousCompleted) return 'locked';

    // Check if the data dependency is met
    if (!hasRequiredData(stepId)) return 'locked';

    // If this step is also completed, mark it
    if (completedSteps.has(stepId)) return 'completed';

    return 'available';
  };

  /**
   * Does this step's own completion condition actually hold?
   *
   * getStepStatus returns 'completed' for anything in `completedSteps`, which is
   * set on visit and is what unlocks the next step. Rendering the green check off
   * that made the stepper claim "4. Approve Terminology" and "5. Generate SOP"
   * were done while nothing was approved and no SOP existed. Unlocking is left
   * exactly as it was; only the check now demands real completion.
   *
   * Returns null when the step has no measurable predicate beyond the user's own
   * sign-off, in which case the sign-off is the completion condition.
   */
  const stepCompletion = (stepId: string): { complete: boolean; reason: string } => {
    const signedOff = completedSteps.has(stepId);
    switch (stepId) {
      case 'add-documents':
        return analysisResults?.document_analyses?.length > 0
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'No documents analyzed yet' };
      case 'review-summaries':
        return analysisResults?.document_analyses?.length > 0 && signedOff
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'Document summaries not reviewed yet' };
      case 'approve-stakeholders':
        return signedOff
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'Stakeholders and systems not approved yet' };
      case 'validate-process-map':
        return approvedMapInfo?.validation_status === 'validated'
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'Process map not approved yet' };
      case 'approve-terminology': {
        if (!terminologySummary || terminologySummary.total === 0) {
          return { complete: false, reason: 'No terminology extracted yet' };
        }
        const { approved, total } = terminologySummary;
        return approved >= total
          ? { complete: true, reason: '' }
          : { complete: false, reason: `${approved} of ${total} terms approved` };
      }
      case 'generate-sop':
        return analysisResults?.sop
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'No SOP generated yet' };
      case 'fill-gaps':
      case 'update-change':
        return analysisResults?.sop && signedOff
          ? { complete: true, reason: '' }
          : { complete: false, reason: 'Not completed yet' };
      default:
        return { complete: signedOff, reason: signedOff ? '' : 'Not completed yet' };
    }
  };

  const tabs = [
    { id: 'add-documents', label: '0. Add Documents', icon: Upload, stepNum: '0' },
    { id: 'review-summaries', label: '1. Review Document Summaries', icon: FileText, stepNum: '1' },
    { id: 'approve-stakeholders', label: '2. Approve Stakeholders and Systems', icon: Users, stepNum: '2' },
    { id: 'validate-process-map', label: '3. Validate Process Map', icon: GitBranch, stepNum: '3' },
    { id: 'approve-terminology', label: '4. Approve Terminology', icon: BookOpen, stepNum: '4' },
    { id: 'generate-sop', label: '5. Generate SOP', icon: Zap, stepNum: '5' },
    { id: 'fill-gaps', label: '6. Fill Gaps', icon: MessageSquare, stepNum: '6' },
    { id: 'update-change', label: '7. Update/Change', icon: GitBranch, stepNum: '7' },
  ];

  const parsedSopContent = React.useMemo(() => {
    const raw = analysisResults?.sop?.content;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return null;
  }, [analysisResults?.sop?.content]);

  const parsedSynthesis = React.useMemo(() => {
    const s = analysisResults?.process_synthesis;
    if (!s || typeof s !== 'object') return null;
    const merge: any = { ...s };
    if (typeof s.synthesis_text === 'string' && s.synthesis_text.trim()) {
      try {
        const parsed = JSON.parse(s.synthesis_text);
        if (parsed && typeof parsed === 'object') {
          for (const k of Object.keys(parsed)) {
            if (merge[k] == null) merge[k] = parsed[k];
          }
          merge._parsed_from_synthesis_text = true;
        }
      } catch {
        merge._synthesis_text_raw = s.synthesis_text;
      }
    }
    return merge;
  }, [analysisResults?.process_synthesis]);

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Please select a project to use SOP Builder</p>
        </div>
      </div>
    );
  }

  // Helper to get lock reason for tooltip
  const getLockReason = (tabId: string): string => {
    const stepIdx = STEP_ORDER.indexOf(tabId);
    const hasDocs = analysisResults?.document_analyses?.length > 0;
    const hasSynthesis = !!analysisResults?.process_synthesis;
    const hasSOP = !!analysisResults?.sop;

    // Check if waiting for data generation
    if (hasDocs && !hasSynthesis && ['approve-stakeholders', 'validate-process-map', 'approve-terminology', 'generate-sop'].includes(tabId)) {
      return 'Generating Synthesis...';
    }
    if (hasSynthesis && !hasSOP && ['fill-gaps', 'update-change'].includes(tabId)) {
      return 'Waiting for SOP...';
    }

    const firstIncomplete = firstIncompleteBlocker(tabId, completedSteps);
    if (firstIncomplete) {
      return `Complete Step ${STEP_ORDER.indexOf(firstIncomplete)} first`;
    }

    return 'Complete previous steps';
  };

  const orchestratorStepLabel = orchestratorCtx.inRouter
    ? tabs.find((t) => t.id === activeTab)?.label || 'SOP Builder step'
    : undefined;

  const orchestratorTargetStep = orchestratorCtx.subStep && STEP_ORDER.includes(orchestratorCtx.subStep)
    ? orchestratorCtx.subStep
    : activeTab;

  const sopSectionsCount = Array.isArray(parsedSopContent?.sections)
    ? parsedSopContent.sections.length
    : (Array.isArray(analysisResults?.sop?.sections) ? analysisResults.sop.sections.length : 0);
  const sopStepsCount = Array.isArray(parsedSopContent?.steps)
    ? parsedSopContent.steps.length
    : (Array.isArray(analysisResults?.sop?.steps) ? analysisResults.sop.steps.length : 0);
  const sopExistsInDb = !!analysisResults?.sop?.id;
  const terminologyTermCount = Array.isArray(analysisResults?.terminology?.terms)
    ? analysisResults.terminology.terms.length
    : 0;
  const interviewAnsweredCount = Array.isArray(analysisResults?.interview_questions)
    ? analysisResults.interview_questions.filter((q: any) => q?.answer && String(q.answer).trim()).length
    : 0;

  const orchestratorCanFinish = (() => {
    if (!orchestratorCtx.inRouter) return false;
    if (orchestratorProject.mismatch) return false;
    switch (orchestratorTargetStep) {
      case 'review-summaries':
        return !!(analysisResults?.document_analyses?.length);
      case 'approve-stakeholders':
        return !!analysisResults?.process_synthesis;
      case 'validate-process-map':
        return !!approvedMapInfo && approvedMapInfo.validation_status === 'validated';
      case 'generate-sop':
        return sopExistsInDb && (sopSectionsCount > 0 || sopStepsCount > 0);
      case 'approve-terminology':
        return terminologyTermCount > 0 || sopExistsInDb;
      case 'fill-gaps':
        return interviewAnsweredCount > 0;
      case 'update-change':
        return sopExistsInDb;
      default:
        return completedSteps.has(orchestratorTargetStep);
    }
  })();

  const orchestratorDisabledReason = (() => {
    if (!orchestratorCtx.inRouter) return undefined;
    if (orchestratorProject.mismatch) {
      return 'You switched projects. Return to the Smart Router plan and reopen this step.';
    }
    switch (orchestratorTargetStep) {
      case 'review-summaries':
        return 'Wait for document analysis to finish before returning to Smart Router.';
      case 'approve-stakeholders':
        return 'Process synthesis has not completed yet — stakeholders will appear once it does.';
      case 'validate-process-map':
        return 'Click Approve Process Map above before finishing.';
      case 'generate-sop':
        return 'Click Generate SOP inside Step 5 and wait for it to produce sections before finishing.';
      case 'approve-terminology':
        return 'Terminology is extracted from your documents automatically. Wait for it to appear, then approve the terms.';
      case 'fill-gaps':
        return 'Answer at least one SME interview question before finishing.';
      case 'update-change':
        return 'Apply or review changes to the SOP before finishing.';
      default:
        return 'Complete this step in SOP Builder before finishing.';
    }
  })();

  const buildOrchestratorPayload = () => {
    if (!orchestratorCtx.inRouter) return null;
    const targetStep = orchestratorCtx.subStep && STEP_ORDER.includes(orchestratorCtx.subStep)
      ? orchestratorCtx.subStep
      : activeTab;
    const tab = tabs.find((t) => t.id === targetStep);
    const refs: Array<Record<string, any>> = [];
    const synth: any = parsedSynthesis || analysisResults?.process_synthesis || null;
    const sopSections = Array.isArray(parsedSopContent?.sections)
      ? parsedSopContent.sections
      : (Array.isArray(analysisResults?.sop?.sections) ? analysisResults.sop.sections : []);
    const sopSteps = Array.isArray(parsedSopContent?.steps)
      ? parsedSopContent.steps
      : (Array.isArray(analysisResults?.sop?.steps) ? analysisResults.sop.steps : []);
    const terminologyTerms = Array.isArray(analysisResults?.terminology?.terms)
      ? analysisResults.terminology.terms
      : [];
    const interviewQuestions = Array.isArray(analysisResults?.interview_questions)
      ? analysisResults.interview_questions
      : [];

    const findings: Array<{ title: string; detail: string; evidence?: string }> = [];
    const tables: Array<{ title: string; columns: string[]; rows: any[][] }> = [];
    const narrativeParts: string[] = [];
    const verifiableTextParts: string[] = [];

    const detailedSteps: any[] = Array.isArray(synth?.detailed_process_steps) ? synth.detailed_process_steps : [];
    const endToEndProcess: string[] = Array.isArray(synth?.end_to_end_process) ? synth.end_to_end_process : [];
    const processGroups: Record<string, string[]> = synth?.process_groups && typeof synth.process_groups === 'object' ? synth.process_groups : {};
    const stakeholdersMap: Record<string, any> = synth?.stakeholders && typeof synth.stakeholders === 'object' ? synth.stakeholders : {};
    const rolesResponsibilities: Record<string, any> = synth?.roles_responsibilities && typeof synth.roles_responsibilities === 'object' ? synth.roles_responsibilities : {};
    const systemsPlatforms: Record<string, any> = synth?.systems_platforms && typeof synth.systems_platforms === 'object' ? synth.systems_platforms : {};
    const controlFramework: string[] = Array.isArray(synth?.control_framework) ? synth.control_framework : [];
    const processFlows: Record<string, string[]> = synth?.process_flows && typeof synth.process_flows === 'object' ? synth.process_flows : {};
    const confidenceAssessment: any = synth?.confidence_assessment || null;
    const templateAlignment: any = synth?.template_alignment || null;

    if (targetStep === 'validate-process-map' && approvedMapInfo) {
      refs.push({
        kind: 'process_map',
        id: approvedMapInfo.id,
        level: approvedMapInfo.level,
        label: `Validated process map (L${approvedMapInfo.level})`,
        validation_status: approvedMapInfo.validation_status,
        approved_at: approvedMapInfo.approved_at,
      });
    }
    if (targetStep === 'generate-sop' && analysisResults?.sop) {
      refs.push({
        kind: 'sop',
        id: analysisResults.sop.id,
        title: analysisResults.sop.title,
        label: `Generated SOP (${sopSectionsCount} sections, ${sopStepsCount} steps)`,
        sections_count: sopSectionsCount,
        steps_count: sopStepsCount,
        compliance_score: analysisResults.sop.compliance_score ?? null,
      });
    }
    if (targetStep === 'approve-terminology' && terminologyTermCount > 0) {
      refs.push({
        kind: 'terminology',
        label: `Approved terminology (${terminologyTermCount} terms)`,
        terms_count: terminologyTermCount,
      });
    }
    if (targetStep === 'fill-gaps' && interviewQuestions.length > 0) {
      refs.push({
        kind: 'interview_responses',
        label: `Gap-filling interviews (${interviewAnsweredCount}/${interviewQuestions.length} answered)`,
        total_questions: interviewQuestions.length,
        answered: interviewAnsweredCount,
      });
    }

    if (endToEndProcess.length > 0) {
      const rows = endToEndProcess.map((s, i) => [String(i + 1), String(s)]);
      tables.push({ title: 'End-to-End Process', columns: ['#', 'Step'], rows });
      narrativeParts.push(
        `## End-to-End Process\n\n${endToEndProcess.map((s, i) => `${i + 1}. ${s}`).join('\n')}`,
      );
      verifiableTextParts.push(endToEndProcess.map((s, i) => `${i + 1}. ${s}`).join('\n'));
    }

    if (detailedSteps.length > 0) {
      const rows = detailedSteps.map((ds: any, i: number) => {
        const step = typeof ds === 'string' ? ds : (ds?.step || ds?.name || `Step ${i + 1}`);
        const evidence = Array.isArray(ds?.sources?.evidence) ? ds.sources.evidence.join(' | ') : '';
        const documents = Array.isArray(ds?.sources?.documents) ? ds.sources.documents.join(', ') : '';
        const confidence = ds?.sources?.confidence || '';
        const notes = ds?.sources?.notes || '';
        return [String(i + 1), String(step), confidence, documents, evidence, notes];
      });
      tables.push({
        title: 'Detailed Process Steps',
        columns: ['#', 'Step', 'Confidence', 'Source Documents', 'Evidence', 'Notes'],
        rows,
      });
      narrativeParts.push(
        `## Detailed Procedure\n\n${detailedSteps
          .map((ds: any, i: number) => {
            const step = typeof ds === 'string' ? ds : (ds?.step || `Step ${i + 1}`);
            const notes = ds?.sources?.notes ? ` — ${ds.sources.notes}` : '';
            return `${i + 1}. ${step}${notes}`;
          })
          .join('\n')}`,
      );
      verifiableTextParts.push(
        detailedSteps
          .map((ds: any, i: number) => {
            const step = typeof ds === 'string' ? ds : (ds?.step || '');
            return `${i + 1}. ${step}`;
          })
          .join('\n'),
      );
    }

    if (Object.keys(processGroups).length > 0) {
      const rows: any[][] = [];
      for (const [group, items] of Object.entries(processGroups)) {
        const list = Array.isArray(items) ? items : [];
        if (list.length === 0) continue;
        rows.push([group, list.join(' | ')]);
      }
      if (rows.length > 0) {
        tables.push({ title: 'Process Groups', columns: ['Process Group', 'Activities'], rows });
      }
    }

    if (Object.keys(stakeholdersMap).length > 0) {
      const rows: any[][] = [];
      for (const [role, responsibility] of Object.entries(stakeholdersMap)) {
        const resp = typeof responsibility === 'string'
          ? responsibility
          : (responsibility as any)?.responsibility || JSON.stringify(responsibility);
        rows.push([role, resp]);
      }
      tables.push({ title: 'Stakeholders', columns: ['Stakeholder', 'Responsibilities'], rows });
    } else if (Object.keys(rolesResponsibilities).length > 0) {
      const rows: any[][] = [];
      for (const [role, info] of Object.entries(rolesResponsibilities)) {
        const resp = typeof info === 'string' ? info : (info as any)?.responsibility || '';
        rows.push([role, resp]);
      }
      tables.push({ title: 'Roles and Responsibilities', columns: ['Role', 'Responsibility'], rows });
    }

    if (Object.keys(systemsPlatforms).length > 0) {
      const rows: any[][] = [];
      for (const [sys, info] of Object.entries(systemsPlatforms)) {
        if (typeof info === 'string') {
          rows.push([sys, info, '', '', '']);
        } else if (info && typeof info === 'object') {
          const i: any = info;
          rows.push([
            sys,
            i.purpose || '',
            i.type || '',
            i.criticality || '',
            Array.isArray(i.integration_points) ? i.integration_points.join(', ') : (i.integration_points || ''),
          ]);
        }
      }
      tables.push({
        title: 'Systems and Platforms',
        columns: ['System', 'Purpose', 'Type', 'Criticality', 'Integration Points'],
        rows,
      });
    }

    if (controlFramework.length > 0) {
      const rows = controlFramework.map((c: string, i: number) => [String(i + 1), String(c)]);
      tables.push({ title: 'Control Framework', columns: ['#', 'Control'], rows });
    }

    if (Object.keys(processFlows).length > 0) {
      const rows: any[][] = [];
      for (const [flow, steps] of Object.entries(processFlows)) {
        const list = Array.isArray(steps) ? steps : [];
        if (list.length === 0) continue;
        rows.push([flow, list.map((s, i) => `${i + 1}. ${s}`).join(' → ')]);
      }
      if (rows.length > 0) {
        tables.push({ title: 'Process Flows', columns: ['Flow', 'Steps'], rows });
      }
    }

    // SOP section narrative is attached for steps that operate on an existing SOP.
    // approve-terminology now runs BEFORE the SOP is generated, so it's excluded
    // here — its payload is the terminology table (built below), not SOP sections.
    if (['generate-sop', 'update-change'].includes(targetStep)) {
      for (const section of sopSections) {
        const title = section?.section_title || section?.title || 'Section';
        let body = '';
        if (typeof section?.content === 'string') {
          body = section.content;
        } else if (Array.isArray(section?.content)) {
          body = section.content
            .map((item: any) => (typeof item === 'string' ? item : ''))
            .filter(Boolean)
            .join('\n\n');
        } else if (section?.content && typeof section.content === 'object') {
          const obj = section.content;
          const lines: string[] = [];
          for (const [k, v] of Object.entries(obj)) {
            if (typeof v === 'string') lines.push(`**${k}:** ${v}`);
            else if (Array.isArray(v)) lines.push(`**${k}:**\n${v.map((x) => `- ${typeof x === 'string' ? x : ''}`).filter(Boolean).join('\n')}`);
          }
          body = lines.join('\n\n');
        }
        if (body) {
          verifiableTextParts.push(body);
          narrativeParts.push(`## ${title}\n\n${body}`);
          findings.push({
            title,
            detail: body.length > 500 ? `${body.slice(0, 500)}…` : body,
            evidence: section?.source || section?.evidence || '',
          });
        }
      }
      if (sopSections.length === 0 && typeof analysisResults?.sop?.content === 'string') {
        try {
          const parsed = JSON.parse(analysisResults.sop.content);
          const title = parsed?.title || analysisResults.sop.title || 'SOP';
          narrativeParts.push(`## ${title}\n\n${parsed?.summary || ''}`);
          verifiableTextParts.push(parsed?.summary || '');
        } catch {
          narrativeParts.push(analysisResults.sop.content);
          verifiableTextParts.push(analysisResults.sop.content);
        }
      }
      if (sopSteps.length > 0) {
        const rows = sopSteps.map((s: any, i: number) => [
          String(i + 1),
          s?.actor || s?.role || 'Actor',
          s?.action || s?.description || '',
          s?.inputs || '',
          s?.outputs || '',
          s?.control || '',
        ]);
        tables.push({
          title: 'SOP Procedure Steps',
          columns: ['#', 'Actor', 'Action', 'Inputs', 'Outputs', 'Control'],
          rows,
        });
      }
    }

    if (targetStep === 'approve-terminology' && terminologyTerms.length > 0) {
      const rows = terminologyTerms.map((t: any) => [t?.term || '', t?.definition || '', t?.category || '']);
      tables.push({ title: 'Terminology', columns: ['Term', 'Definition', 'Category'], rows });
      verifiableTextParts.push(rows.map(([term, def]: [string, string]) => `${term}: ${def}`).join('\n'));
    }

    if (targetStep === 'fill-gaps' && interviewQuestions.length > 0) {
      const rows = interviewQuestions
        .filter((q: any) => q?.answer && String(q.answer).trim())
        .map((q: any) => [q?.question || '', q?.answer || '', q?.category || '', q?.notes || '']);
      if (rows.length > 0) {
        tables.push({
          title: 'SME Interview Responses',
          columns: ['Question', 'Answer', 'Category', 'Notes'],
          rows,
        });
        verifiableTextParts.push(rows.map(([q, a]: [string, string]) => `Q: ${q}\nA: ${a}`).join('\n\n'));
      }
    }

    const execSummaryLines: string[] = [];
    if (approvedMapInfo) execSummaryLines.push(`• Validated Level ${approvedMapInfo.level} process map${approvedMapInfo.approved_at ? ` on ${new Date(approvedMapInfo.approved_at).toLocaleDateString()}` : ''}.`);
    if (endToEndProcess.length) execSummaryLines.push(`• End-to-end process covers ${endToEndProcess.length} discrete activities.`);
    if (Object.keys(stakeholdersMap).length || Object.keys(rolesResponsibilities).length) {
      execSummaryLines.push(`• ${Object.keys(stakeholdersMap).length || Object.keys(rolesResponsibilities).length} stakeholder role(s) identified with clear responsibilities.`);
    }
    if (Object.keys(systemsPlatforms).length) execSummaryLines.push(`• ${Object.keys(systemsPlatforms).length} system(s) / platform(s) mapped with integration points and criticality.`);
    if (controlFramework.length) execSummaryLines.push(`• ${controlFramework.length} control(s) embedded across the process lifecycle.`);
    if (sopExistsInDb) execSummaryLines.push(`• SOP drafted with ${sopSectionsCount} section(s) and ${sopStepsCount} procedure step(s).`);
    if (terminologyTerms.length) execSummaryLines.push(`• Business glossary covers ${terminologyTerms.length} term(s) agreed with subject-matter experts.`);
    if (interviewAnsweredCount) execSummaryLines.push(`• ${interviewAnsweredCount} gap-filling SME interview(s) completed and captured.`);
    if (confidenceAssessment?.overall_score != null) execSummaryLines.push(`• Process synthesis confidence rated at ${confidenceAssessment.overall_score}%.`);

    const execSummary = execSummaryLines.length > 0
      ? `## Executive Summary\n\n${execSummaryLines.join('\n')}`
      : '';

    if (execSummary) narrativeParts.unshift(execSummary);

    const gaps: string[] = [];
    if (Array.isArray(synth?.gaps)) gaps.push(...synth.gaps.filter((g: any) => typeof g === 'string'));
    if (Array.isArray(confidenceAssessment?.low_confidence)) gaps.push(...confidenceAssessment.low_confidence);
    if (Array.isArray(templateAlignment?.deviation_areas)) gaps.push(...templateAlignment.deviation_areas);

    const analysisPayload = {
      narrative: narrativeParts.join('\n\n').slice(0, 120000),
      findings,
      tables,
      gaps: Array.from(new Set(gaps)).slice(0, 50),
    };

    return {
      artifact: {
        refs,
        output_payload: {
          target_step: targetStep,
          project_id: currentProject?.id ?? null,
          analysis: analysisPayload,
          analysis_narrative: analysisPayload.narrative,
          executive_summary: execSummaryLines,
          sop: analysisResults?.sop
            ? {
                id: analysisResults.sop.id,
                title: analysisResults.sop.title,
                sections: sopSections,
                steps: sopSteps,
                compliance_score: analysisResults.sop.compliance_score ?? null,
              }
            : null,
          process_map: approvedMapInfo
            ? {
                id: approvedMapInfo.id,
                level: approvedMapInfo.level,
                validation_status: approvedMapInfo.validation_status,
                approved_at: approvedMapInfo.approved_at,
              }
            : null,
          process_synthesis_structured: synth
            ? {
                end_to_end_process: endToEndProcess,
                detailed_process_steps: detailedSteps,
                process_groups: processGroups,
                stakeholders: stakeholdersMap,
                roles_responsibilities: rolesResponsibilities,
                systems_platforms: systemsPlatforms,
                control_framework: controlFramework,
                process_flows: processFlows,
                confidence_assessment: confidenceAssessment,
                template_alignment: templateAlignment,
              }
            : null,
          terminology: terminologyTerms.length > 0
            ? { terms: terminologyTerms }
            : null,
          interview_responses: interviewQuestions.length > 0
            ? interviewQuestions.map((q: any) => ({
                question: q?.question || '',
                answer: q?.answer || '',
                category: q?.category || '',
                notes: q?.notes || '',
              }))
            : [],
          analysis_summary: {
            has_document_analyses: !!(analysisResults?.document_analyses?.length),
            has_synthesis: !!synth,
            has_sop: !!analysisResults?.sop,
            has_terminology: terminologyTerms.length > 0,
          },
        },
        verifiable_text: verifiableTextParts.join('\n\n').slice(0, 50000),
      },
      summary: `Finished SOP Builder step "${tab?.label || targetStep}".`,
    };
  };

  return (
    <div className="space-y-6">
      {orchestratorCtx.inRouter && (
        <SmartRouterBanner
          stepLabel={orchestratorStepLabel}
          canFinish={orchestratorCanFinish}
          disabledReason={orchestratorDisabledReason}
          buildCompletionPayload={async () => {
            const payload = buildOrchestratorPayload();
            if (!payload) return null;
            await smartRouter.finishStep(payload.artifact, payload.summary);
            return null;
          }}
          buildResumeExtra={() => ({ subStep: activeTab })}
        />
      )}
      <OrchestratorProjectMismatchBanner guard={orchestratorProject} />

      {/* Inactive (expired / archived) project warning — prompts the user to
          switch instead of silently failing downstream (e.g. collaborators 404). */}
      {currentProject && projectInactiveReason(currentProject) !== null && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-900 font-medium">
              This project is {projectInactiveLabel(projectInactiveReason(currentProject)).toLowerCase()}
            </p>
            <p className="text-amber-800 text-sm">
              "{currentProject.name}" is no longer active, so some features may not work. Pick an active project from the project menu at the top to continue.
            </p>
          </div>
        </div>
      )}

      {currentProject && !loading && projectInactiveReason(currentProject) === null && currentProject.is_collaborator === true && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          <Users className="w-4 h-4 flex-shrink-0" />
          <span>You are a collaborator on this project — full access to all features.</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <nav className="flex space-x-2 p-2 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const status = getStepStatus(tab.id);
            const isLocked = status === 'locked';
            // Three distinct states: locked (padlock), in progress (the step's own
            // icon), complete (green check). The check is earned, not granted on
            // visit — see stepCompletion.
            const completion = stepCompletion(tab.id);
            const isCompleted = !isLocked && completion.complete;
            const isInProgress = !isLocked && !completion.complete;

            // Check waiting states for visual feedback
            const hasDocs = analysisResults?.document_analyses?.length > 0;
            const hasSynthesis = !!analysisResults?.process_synthesis;
            const hasSOP = !!analysisResults?.sop;
            const isWaitingForData = isLocked && (
              (hasDocs && !hasSynthesis && ['approve-stakeholders', 'validate-process-map', 'approve-terminology', 'generate-sop'].includes(tab.id)) ||
              (hasSynthesis && !hasSOP && ['fill-gaps', 'update-change'].includes(tab.id))
            );

            const lockReason = isLocked ? getLockReason(tab.id) : '';

            return (
              <button
                key={tab.id}
                onClick={() => !isLocked && setActiveTab(tab.id)}
                disabled={isLocked}
                title={isLocked ? lockReason : (isInProgress ? `In progress — ${completion.reason}` : '')}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap relative ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : isLocked
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : isLocked ? (
                  isWaitingForData ? (
                    <Loader className="w-4 h-4 animate-spin text-blue-500" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )
                ) : (
                  <Icon className={`w-4 h-4 ${activeTab === tab.id ? '' : 'text-amber-500'}`} />
                )}
                <span>{tab.label}</span>
                {isLocked && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ml-1 ${
                    isWaitingForData
                      ? 'bg-blue-100 text-blue-700 animate-pulse'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {lockReason}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'add-documents' && (
        <>
          {/* Documents Upload Section — Project Instructions + Org Standards badge live in its toolbar row */}
          <Documents
            toolbarExtra={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setIsProjectInstructionsCollapsed(!isProjectInstructionsCollapsed)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <ClipboardList className="w-4 h-4 text-gray-500" />
                    <span>Project Instructions</span>
                    {projectInstructions && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${!isProjectInstructionsCollapsed ? 'rotate-180' : ''}`} />
                  </button>

                  {!isProjectInstructionsCollapsed && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsProjectInstructionsCollapsed(true)} />
                      <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Project Instructions</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Applies to all documents (document-level instructions override these)</p>
                          </div>
                          <button
                            onClick={() => setIsProjectInstructionsCollapsed(true)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>

                        {editingProjectInstructions ? (
                          <div className="space-y-3">
                            <textarea
                              value={projectInstructions}
                              onChange={e => setProjectInstructions(e.target.value)}
                              placeholder="e.g., Focus on process controls, ignore historical data, highlight automation opportunities..."
                              className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={5}
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => {
                                  setEditingProjectInstructions(false);
                                  setProjectInstructions(currentProject?.analysis_instructions || '');
                                }}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={saveProjectInstructions}
                                disabled={savingProjectInstructions}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition flex items-center gap-2"
                              >
                                {savingProjectInstructions && <Loader className="w-3.5 h-3.5 animate-spin" />}
                                <span>{savingProjectInstructions ? 'Saving...' : 'Save'}</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="bg-gray-50 rounded-lg p-3 min-h-16 max-h-48 overflow-y-auto">
                              {projectInstructions ? (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{projectInstructions}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">No project instructions set yet.</p>
                              )}
                            </div>
                            <div className="flex justify-end">
                              <button
                                onClick={() => setEditingProjectInstructions(true)}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="relative">
                  <button
                    onClick={() => setIsOrgStandardsPanelOpen(!isOrgStandardsPanelOpen)}
                    className="px-3 py-1.5 text-sm border border-green-300 bg-green-50 text-green-800 rounded-lg hover:bg-green-100 flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <BookOpen className="w-4 h-4 text-green-600" />
                    <span>
                      Org Standards {tenantDocs.length - excludedOrgStandardIds.size > 0 ? 'Applied' : 'Available'}
                    </span>
                    {tenantDocs.length - excludedOrgStandardIds.size > 0 && (
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    )}
                  </button>

                  {isOrgStandardsPanelOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsOrgStandardsPanelOpen(false)} />
                      <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Organization Standards Library</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Toggle which standards are included in SOP generation for this project</p>
                          </div>
                          <button
                            onClick={() => setIsOrgStandardsPanelOpen(false)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>

                        {tenantDocsLoading ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                            <span>Loading standards…</span>
                          </div>
                        ) : tenantDocsError ? (
                          <div className="text-sm text-red-600 flex items-center gap-1.5 py-2">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>Could not load standards: {tenantDocsError}</span>
                          </div>
                        ) : tenantDocs.length === 0 ? (
                          <p className="text-sm text-gray-400 italic py-2">No standards uploaded yet. An admin can add them via Security Center → Tenant Management → Standards Library.</p>
                        ) : (
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {tenantDocs.map(doc => {
                              const isExcluded = excludedOrgStandardIds.has(doc.id);
                              return (
                                <div key={doc.id} className="flex items-center justify-between gap-2 px-2 py-2 rounded hover:bg-gray-50">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${isExcluded ? 'text-gray-300' : 'text-green-500'}`} />
                                    <span className={`text-sm truncate ${isExcluded ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{doc.original_name}</span>
                                    {doc.processing_status !== 'completed' && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 capitalize flex-shrink-0">
                                        {doc.processing_status}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => toggleOrgStandard(doc.id)}
                                    role="switch"
                                    aria-checked={!isExcluded}
                                    title={isExcluded ? 'Excluded from SOP generation — click to include' : 'Included in SOP generation — click to exclude'}
                                    className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors ${isExcluded ? 'bg-gray-300' : 'bg-green-500'}`}
                                  >
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isExcluded ? '' : 'translate-x-4'}`} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {currentProject && projectInactiveReason(currentProject) === null && (
                  <CollaboratorsList
                    variant="dropdown"
                    projectId={currentProject.id}
                    /* A co-owner is an owner too, so this reads the flag rather
                       than assuming whoever opened the page owns the project. */
                    isOwner={currentProject.is_owner === true}
                    onError={(message) => toast.error(message)}
                  />
                )}
              </div>
            }
          />
        </>
      )}

      {activeTab === 'review-summaries' && (
        <Analysis />
      )}

      {activeTab === 'approve-stakeholders' && (
        <>
          {!analysisResults?.process_synthesis ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
              <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-yellow-900 mb-2">Process Synthesis Required</h3>
              <p className="text-yellow-700 mb-4">
                Stakeholders & Systems data is generated after Process Synthesis is complete.
              </p>
              <p className="text-yellow-600 text-sm mb-6">
                Please go to <strong>Step 1: Review Document Summaries</strong> → Click on <strong>Process Synthesis</strong> tab → Generate the synthesis to unlock this step.
              </p>
              <button
                onClick={() => setActiveTab('review-summaries')}
                className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Go to Document Summaries</span>
              </button>
            </div>
          ) : (
            <>
              <StakeholdersSystemsTab
                synthesis={analysisResults?.process_synthesis}
                isAnalyzing={false}
                analysisProgress={null}
                onLoadingChange={setStakeholdersLoading}
              />
              <div className="mt-6 flex justify-end">
                {!stakeholdersLoading && completedSteps.has('approve-stakeholders') ? (
                  <div className="flex items-center space-x-3">
                    <span className="text-green-600 text-sm font-medium flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4" />
                      <span>Stakeholders & Systems approved</span>
                    </span>
                    <button
                      onClick={() => {
                        setCompletedSteps(prev => {
                          const next = new Set(prev);
                          next.delete('approve-stakeholders');
                          saveWorkflowState({ completed_steps: Array.from(next) });
                          return next;
                        });
                      }}
                      className="text-sm text-gray-500 underline hover:text-gray-700"
                    >
                      Edit
                    </button>
                  </div>
                ) : !stakeholdersLoading ? (
                  <button
                    onClick={() => {
                      markStepCompleted('approve-stakeholders');
                      toast.success('Stakeholders & Systems approved');
                    }}
                    className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Approve Stakeholders & Systems</span>
                  </button>
                ) : null}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'validate-process-map' && (
        <div className="space-y-4">
          {/*
            The "Process Map Pending Approval" banner and its green Approve button
            used to live here. Both are now step 4 of ProcessMapValidationBar inside
            ProcessMaps, so the review chain reads as one ordered control group
            instead of a banner plus four scattered toolbar buttons.
          */}
          {!approvedMapInfo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                Generate an L1 or L3 map to get started. Note: L3 must be generated before L2.
              </p>
            </div>
          )}
          <ProcessMaps
            approvalInfo={approvedMapInfo}
            approving={approving}
            onApprove={handleApproveProcessMap}
            onRevokeApproval={handleUnapproveProcessMap}
            onApprovalStateChanged={loadProcessMapApproval}
          />
        </div>
      )}

      {activeTab === 'generate-sop' && (
        <>
          <SOPGenerationTab
            analysisResults={analysisResults}
            loadAnalysisResults={loadAnalysisResults}
            isAnalyzing={false}
            analysisProgress={null}
            globalComplianceScore={0}
            onRefresh={loadAnalysisResults}
            approvedMapInfo={approvedMapInfo}
            onGoToProcessMap={() => setActiveTab('validate-process-map')}
            onGoToTerminology={() => setActiveTab('approve-terminology')}
          />
        </>
      )}

      {activeTab === 'approve-terminology' && (
        <>
          {!analysisResults?.process_synthesis ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
              <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-yellow-900 mb-2">Process Synthesis Required</h3>
              <p className="text-yellow-700 mb-4">
                Terminology analysis runs automatically once document analysis and process synthesis are complete.
                Business terms are extracted from your uploaded documents as part of the workflow.
              </p>
              <p className="text-yellow-600 text-sm mb-6">
                Please go to <strong>Step 1: Review Document Summaries</strong> to generate the synthesis first.
              </p>
              <button
                onClick={() => setActiveTab('review-summaries')}
                className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>Go to Review Document Summaries</span>
              </button>
            </div>
          ) : (
            <TerminologyAnalysis
              analysisResults={analysisResults}
              onRefresh={async () => {
                if (!currentProject) return;
                try {
                  const api = (await import('../api')).default;
                  const response = await api.get(`/api/analysis/results/${currentProject.id}`);
                  setAnalysisResults(response.data);
                } catch (error) {
                  console.error('Failed to refresh analysis results:', error);
                }
              }}
            />
          )}
        </>
      )}

      {activeTab === 'fill-gaps' && (
        <Interviews />
      )}

      {activeTab === 'update-change' && (
        <SOPUpdateChange
          sop={analysisResults?.sop}
          onApplied={loadAnalysisResults}
        />
      )}
    </div>
  );
};

export default SOPBuilder;
